/*
p5.bezier library by Peiling Jiang
2020

updated Aug 2024
*/
var __spreadArray = (this && this.__spreadArray) || function (to, from, pack) {
    if (pack || arguments.length === 2) for (var i = 0, l = from.length, ar; i < l; i++) {
        if (ar || !(i in from)) {
            if (!ar) ar = Array.prototype.slice.call(from, 0, i);
            ar[i] = from[i];
        }
    }
    return to.concat(ar || Array.prototype.slice.call(from));
};
import packageJson from '../package.json';
import { _binomialCoefficient, _smoothness, } from './coefficients';
import { _concentrate, _dist, _getCanvasUtils, _getCloseCurvePoints, _getDimension, _interpolateVertex, _setStyles, _validateSmoothness, } from './utils';
window.console.log("[p5.bezier] ".concat(packageJson.version));
/* -------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------- */
// helpers
function _bezierVertex(pointList, n, t, dimension) {
    var vertex = new Array(dimension).fill(0);
    var oneMinusT = 1 - t;
    for (var i = 0; i <= n; i++) {
        var coefficient = _binomialCoefficient(n, i) * Math.pow(oneMinusT, (n - i)) * Math.pow(t, i);
        for (var d = 0; d < dimension; d++)
            vertex[d] += coefficient * pointList[i][d];
    }
    return vertex;
}
function _drawBezierCurve(bezierCanvas, pointList, smoothness) {
    var n = pointList.length - 1;
    var increment = _smoothness[_validateSmoothness(smoothness)];
    for (var t = 0; t <= 1; t += increment) {
        var v = _bezierVertex(pointList, n, t, bezierCanvas.dimension);
        bezierCanvas.lineTo.apply(bezierCanvas, v);
    }
}
// function _drawBSplineCurve(
//   bezierCanvas: BezierCanvas,
//   pointList: PointList,
//   smoothness: Smoothness,
// ): void {
//   const increment = _smoothness[_validateSmoothness(smoothness)]
//   const n = pointList.length - 1
//   for (let i = 0; i < n - 2; i++) {
//     const b0 = pointList[i]
//     const b1 = pointList[i + 1]
//     const b2 = pointList[i + 2]
//     const b3 = pointList[i + 3]
//     for (let t = 0; t <= 1; t += increment) {
//       const t2 = t * t
//       const t3 = t2 * t
//       const vertex = bezierCanvas.dimension === 2 ? [0, 0] : [0, 0, 0]
//       for (let d = 0; d < bezierCanvas.dimension; d++) {
//         vertex[d] =
//           (1 / 6) *
//           ((-b0[d] + 3 * b1[d] - 3 * b2[d] + b3[d]) * t3 +
//             (3 * b0[d] - 6 * b1[d] + 3 * b2[d]) * t2 +
//             (-3 * b0[d] + 3 * b2[d]) * t +
//             (b0[d] + 4 * b1[d] + b2[d]))
//       }
//       bezierCanvas.lineTo(vertex[0], vertex[1], vertex[2])
//     }
//   }
// }
/* -------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------- */
/* -------------------------------------------------------------------------- */
var P5Bezier = /** @class */ (function () {
    // biome-ignore lint/suspicious/noExplicitAny: p5 typing
    function P5Bezier(canvas) {
        this.b = {
            canvas: null,
            ctx: null,
            dimension: 2,
            useP5: true,
            beginPath: function () { },
            moveTo: function () { },
            lineTo: function () { },
            closePath: function () { },
        };
        this.b.canvas = canvas;
        this.b.ctx = this.b.canvas.drawingContext;
        if (typeof p5 !== 'undefined' && canvas instanceof p5.Graphics) {
            this.b.useP5 = true;
            this.b.dimension = _getDimension(this.b.ctx, false);
        }
        else if ((typeof p5 !== 'undefined' && canvas instanceof p5.Renderer) ||
            canvas.drawingContext) {
            this.b.useP5 = false;
            if (typeof p5 === 'undefined' ||
                (typeof p5 !== 'undefined' && !(canvas instanceof p5.Renderer)))
                window.console.warn('[p5.bezier] Support beyond p5.js is not tested');
            this.b.dimension = _getDimension(this.b.ctx, canvas.isP3D);
        }
        else
            throw new Error('[p5.bezier] Canvas is not supported');
        _getCanvasUtils(this.b);
    }
    P5Bezier.prototype.draw = function (pointList, closeType, smoothness) {
        var _a, _b;
        if (closeType === void 0) { closeType = 'OPEN'; }
        if (smoothness === void 0) { smoothness = 3; }
        if (pointList.length < 2) {
            throw new Error('[p5.bezier] At least 2 points are needed to draw a curve');
        }
        var _pL = closeType === 'CLOSE'
            ? __spreadArray(__spreadArray([], _concentrate(pointList, true), true), _getCloseCurvePoints(pointList), true) : _concentrate(pointList);
        this.b.beginPath();
        (_a = this.b).moveTo.apply(_a, _pL[0]);
        _drawBezierCurve(this.b, _pL, smoothness);
        (_b = this.b).lineTo.apply(_b, _pL[_pL.length - 1]);
        if (this.b.useP5)
            this.b.closePath(closeType);
        else if (closeType === 'CLOSE')
            this.b.closePath();
        _setStyles(this.b);
        return _pL;
    };
    // bSpline(
    //   pointList: PointList,
    //   closeType: CloseType = 'OPEN',
    //   smoothness: Smoothness = 3,
    // ): PointList {
    //   if (pointList.length < 2) {
    //     throw new Error(
    //       '[p5.bezier] At least 2 points are needed to draw a curve',
    //     )
    //   }
    //   const _pL =
    //     closeType === 'CLOSE'
    //       ? [...pointList, ...pointList.slice(0, 3)]
    //       : pointList
    //   this.b.beginPath()
    //   // this.b.moveTo(..._pL[0])
    //   _drawBSplineCurve(this.b, _pL, smoothness)
    //   // this.b.lineTo(..._pL[_pL.length - 1])
    //   if (this.b.useP5) {
    //     this.b.closePath(closeType)
    //   } else if (closeType === 'CLOSE') {
    //     this.b.closePath()
    //   }
    //   _setStyles(this.b)
    //   return _pL
    // }
    P5Bezier.prototype.new = function (pointList, closeType, smoothness) {
        if (closeType === void 0) { closeType = 'OPEN'; }
        if (smoothness === void 0) { smoothness = 3; }
        var increment = _smoothness[_validateSmoothness(smoothness)];
        return new BezierCurve(pointList, closeType, increment, this.b);
    };
    return P5Bezier;
}());
// biome-ignore lint/suspicious/noExplicitAny: p5 typing
function initBezier(canvas) {
    return new P5Bezier(canvas);
}
/* -------------------------------------------------------------------------- */
var BezierCurve = /** @class */ (function () {
    function BezierCurve(points, closeType, increment, bezierCanvas, vertexList) {
        var _a;
        if (vertexList === void 0) { vertexList = null; }
        this.controlPoints = _concentrate(points, closeType === 'CLOSE');
        if (closeType === 'CLOSE') {
            (_a = this.controlPoints).push.apply(_a, _getCloseCurvePoints(this.controlPoints));
            this.closeType = 'CLOSE';
        }
        else
            this.closeType = 'OPEN';
        this.dimension = bezierCanvas.dimension;
        this.increment = increment;
        this.vertexList = [];
        this.p = this.controlPoints.length; // has p points for (p - 1) degree curves
        this.n = this.p - 1; // degree
        this.b = bezierCanvas;
        if (vertexList === null)
            this._buildVertexList();
        else
            this.vertexList = __spreadArray([], vertexList, true);
    }
    BezierCurve.prototype._buildVertexList = function () {
        this.vertexList = [];
        for (var t = 0; t <= 1; t += this.increment) {
            var v = _bezierVertex(this.controlPoints, this.n, t, this.dimension);
            this.vertexList.push(v);
        }
        this._addVertex(this.controlPoints[this.controlPoints.length - 1]);
        this.dimension = this.vertexList[0].length;
        return this.vertexList;
    };
    BezierCurve.prototype._addVertex = function (vArray) {
        var _a;
        (_a = this.b).lineTo.apply(_a, vArray);
    };
    BezierCurve.prototype._distVertex = function (vArray1, vArray2) {
        return this.dimension === 3 && vArray1.length === 3 && vArray2.length === 3
            ? _dist(vArray1[0], vArray1[1], vArray1[2], vArray2[0], vArray2[1], vArray2[2])
            : this.dimension === 2
                ? _dist(vArray1[0], vArray1[1], vArray2[0], vArray2[1])
                : 0;
    };
    BezierCurve.prototype.draw = function (dash) {
        if (!dash) {
            this._solidCurve();
        }
        else {
            this._dashedCurve(dash);
        }
    };
    BezierCurve.prototype._solidCurve = function () {
        var _this = this;
        this.b.beginPath();
        this.vertexList.map(function (v) { return _this._addVertex(v); });
        if (this.closeType === 'CLOSE')
            this.b.closePath();
        if (this.b.useP5)
            this.b.closePath(this.closeType);
        else if (this.closeType === 'CLOSE')
            this.b.closePath();
        _setStyles(this.b);
    };
    BezierCurve.prototype._dashedCurve = function (dash) {
        var _a, _b, _c, _d, _e;
        if (this.increment > 0.001) {
            this.increment = 0.001;
            window.console.warn('[p5.bezier] Smoothness set to 3 for a dashed curve');
        }
        var _f = dash.map(Math.abs), solidPart = _f[0], gapPart = _f[1];
        var solid = true;
        var lastVertex = this.vertexList[0];
        var toUseVertexInd = 1;
        var currentVirtualVertex = lastVertex;
        var availableDist = 0;
        var neededDist = solidPart;
        this.b.ctx.save();
        this.b.ctx.fillStyle = 'rgba(0, 0, 0, 0)';
        this.b.beginPath();
        (_a = this.b).moveTo.apply(_a, lastVertex);
        while (toUseVertexInd < this.vertexList.length) {
            var toUseVertex = this.vertexList[toUseVertexInd];
            availableDist = this._distVertex(lastVertex, toUseVertex);
            while (availableDist >= neededDist) {
                currentVirtualVertex = _interpolateVertex(currentVirtualVertex, toUseVertex, neededDist / availableDist);
                solid
                    ? (_b = this.b).lineTo.apply(_b, currentVirtualVertex) : (_c = this.b).moveTo.apply(_c, currentVirtualVertex);
                availableDist -= neededDist;
                solid = !solid;
                neededDist = solid ? solidPart : gapPart;
            }
            solid ? (_d = this.b).lineTo.apply(_d, toUseVertex) : (_e = this.b).moveTo.apply(_e, toUseVertex);
            neededDist -= this._distVertex(currentVirtualVertex, toUseVertex);
            lastVertex = toUseVertex;
            currentVirtualVertex = lastVertex;
            toUseVertexInd++;
        }
        _setStyles(this.b);
        this.b.ctx.restore();
    };
    BezierCurve.prototype.update = function (newControlPointList) {
        if (newControlPointList.length !== this.controlPoints.length) {
            throw new Error('[p5.bezier] The number of control points changed');
        }
        if (this.controlPoints.every(function (v, i) { return v === newControlPointList[i]; })) {
            return;
        }
        this.controlPoints = newControlPointList;
        this._buildVertexList();
    };
    BezierCurve.prototype.move = function (x, y, z, toDraw, dash) {
        if (z === void 0) { z = null; }
        if (toDraw === void 0) { toDraw = true; }
        if (z === null && this.dimension === 3) {
            throw new Error('[p5.bezier] X, Y, and Z are needed to move a 3D curve');
        }
        var toMove = [x, y];
        if (z !== null)
            toMove.push(z);
        var newCurveV = this.vertexList.map(function (v) { return v.slice(); });
        var newCurveObj = new BezierCurve(this.controlPoints, this.closeType, this.increment, this.b, newCurveV);
        newCurveObj.vertexList = newCurveObj.vertexList.map(function (v) { return v.map(function (val, i) { return val + toMove[i]; }); });
        if (toDraw)
            newCurveObj.draw(dash);
        return newCurveObj;
    };
    BezierCurve.prototype.shortest = function (pX, pY, pZ) {
        var _this = this;
        if (pZ === void 0) { pZ = 0; }
        var minVertex = [0, 0, 0];
        var dMin = Number.POSITIVE_INFINITY;
        this.vertexList.map(function (v) {
            var nowMin = _this._distVertex(v, [pX, pY, pZ]);
            if (dMin > nowMin) {
                dMin = nowMin;
                minVertex = __spreadArray([], v, true);
            }
        });
        return minVertex;
    };
    return BezierCurve;
}());
export default initBezier;
